import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/home';
import { getArticlesByCategory } from '@/mocks/articles';
import useSEO from '@/hooks/useSEO';

const categoryMeta: Record<string, { name: string; icon: string; description: string; image: string }> = {};
categories.forEach((c) => {
  categoryMeta[c.slug] = {
    name: c.name,
    icon: c.icon,
    description: c.description,
    image: c.image,
  };
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const meta = slug ? categoryMeta[slug] : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  const canonicalUrl = `http://peachbeat.cn/category/${slug}`;

  useEffect(() => {
    if (meta) {
      document.title = `${meta.name} - 51黑料网黑料曝光平台`;
    } else {
      document.title = '分类未找到 - 51黑料网';
    }
  }, [meta]);

  useSEO({
    title: meta
      ? `${meta.name} - 51黑料网黑料曝光 | 每日最新${meta.name}揭秘`
      : '分类未找到 - 51黑料网',
    description: meta
      ? `${meta.description} 51黑料网${meta.name}分类汇集了${articles.length}篇独家黑料曝光文章，每日更新最新${meta.name}内幕，为您揭开真相。`
      : '该分类可能不存在或已被移除',
    canonicalUrl,
    ogImage: meta?.image,
    ogType: 'website',
  });

  if (!meta) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar transparent={false} />
        <main className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <i className="ri-folder-unknow-line text-6xl text-foreground-600 mb-4"></i>
          <h1 className="text-2xl font-heading font-bold text-foreground-100 mb-2">分类未找到</h1>
          <p className="text-foreground-400 mb-6">该分类可能不存在或已被移除</p>
          <Link
            to="/"
            className="px-6 py-3 bg-primary-500 text-foreground-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${meta.name} - 51黑料网`,
    description: meta.description,
    url: `http://peachbeat.cn/category/${slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '51黑料网',
      url: 'http://peachbeat.cn/',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: articles.length,
      itemListElement: articles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Article',
          headline: a.title,
          description: a.excerpt,
          image: a.image,
          url: `http://peachbeat.cn/article/${a.slug}`,
        },
      })),
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar transparent={false} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      {/* Category Header */}
      <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
        <img
          src={meta.image}
          alt={`51黑料网 - ${meta.name}`}
          className="w-full h-full object-cover object-top"
          title={`${meta.name} - 51黑料网`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-black/30"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/20 text-accent-400">
              <i className={`${meta.icon} text-xl`}></i>
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50">{meta.name}</h1>
          </div>
          <p className="text-sm md:text-base text-foreground-300 max-w-xl">{meta.description}</p>
        </div>
      </section>

      {/* Breadcrumb + Stats */}
      <section className="bg-background-100 border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-foreground-300 transition-colors duration-200 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-200">{meta.name}</span>
          </div>
          <span className="text-sm text-foreground-400">
            共 <strong className="text-foreground-200">{articles.length}</strong> 篇曝光文章
          </span>
        </div>
      </section>

      {/* Articles List */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/40 hover:border-accent-500/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      title={article.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-100/90 to-transparent"></div>
                    {article.hot && (
                      <span className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-medium bg-primary-500/90 text-foreground-50 flex items-center gap-1">
                        <i className="ri-fire-line"></i> HOT
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <h2 className="text-sm md:text-base font-semibold text-foreground-100 group-hover:text-accent-400 transition-colors duration-200 line-clamp-2 leading-snug">
                      {article.title}
                    </h2>
                    <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200/40">
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-time-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-eye-line"></i>
                        {(article.views / 1000).toFixed(1)}k 阅读
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <i className="ri-inbox-line text-5xl text-foreground-600 mb-4"></i>
              <h3 className="text-lg font-semibold text-foreground-300 mb-2">暂无文章</h3>
              <p className="text-sm text-foreground-500 mb-6">该分类下暂时还没有文章，请稍后再来</p>
              <Link
                to="/"
                className="px-5 py-2.5 border border-background-300/60 rounded-full text-sm text-foreground-400 hover:text-foreground-200 hover:border-foreground-300 transition-colors duration-200 cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}

          {/* Pagination placeholder */}
          {articles.length > 9 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200/60 text-foreground-500 text-sm cursor-pointer hover:border-foreground-400 transition-colors duration-200" disabled>
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-foreground-50 text-sm font-medium cursor-pointer">1</button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200/60 text-foreground-400 text-sm cursor-pointer hover:border-foreground-400 transition-colors duration-200">2</button>
              <button className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200/60 text-foreground-400 text-sm cursor-pointer hover:border-foreground-400 transition-colors duration-200">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Other Categories */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h3 className="text-lg font-heading font-bold text-foreground-200 mb-6">浏览其他分类</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {categories
              .filter((c) => c.slug !== slug)
              .map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background-50 border border-background-200/40 text-foreground-400 hover:text-accent-400 hover:border-accent-500/30 transition-all duration-200 cursor-pointer"
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  <span className="text-sm whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
